import { keepPreviousData, useQuery } from "@tanstack/react-query"
import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import { useTRPCClient } from "utils/trpc"

/**
 * Returns a list of metric breakdowns for a project.
 * @example
 * const { data: metricBreakdowns } = useMetricBreakdown({
 *   dataIds: ["aave"],
 *   metricIds: ["fees"],
 *   interval: "30d",
 * })
 */

export const useMetricBreakdown = ({
  dataIds,
  metricIds,
  chainIds,
  productIds,
  interval,
  groupBy = "projects",
  enabled = true,
}: {
  dataIds?: string[]
  metricIds: string[]
  chainIds?: string[]
  productIds?: string[]
  interval: Interval
  groupBy:
    | "projects"
    | "chain-project"
    | "chain-market-sector"
    | "chains"
    | "market-sector"
    | "product"
    | "product+chain"
  enabled?: boolean
}) => {
  const trpcClient = useTRPCClient()

  return useQuery({
    queryKey: [
      "metric-breakdown",
      dataIds,
      metricIds,
      interval,
      groupBy,
      chainIds,
      productIds,
    ],
    queryFn: () =>
      trpcClient.metrics.postBreakdown.mutate(
        {
          data_ids: !dataIds || dataIds?.length > 0 ? dataIds : undefined,
          metric_ids: metricIds,
          chain_ids: !chainIds || chainIds?.length > 0 ? chainIds : undefined,
          product_ids:
            !!productIds && productIds.length > 0 ? productIds : undefined,
          // @ts-ignore - bad zod inference
          interval,
          groupBy,
        },
        {
          context: {
            skipBatch: true,
          },
        }
      ),
    enabled: enabled && !!metricIds.length,
    placeholderData: keepPreviousData,
  })
}
